<template>
  <div class="datePicker input-group" :qaid="qaid">
    <span v-if="allowIcon" class="input-group-label">
      <i class="material-icons">{{ enableTime ? 'access_time' : 'date_range' }}</i>
    </span>
    <input
      :id="inputId"
      ref="picker"
      class="datePicker__input form-control flatpickr"
      type="text"
      :name="name"
      :placeholder="placeholder"
      data-input
      @input="onInput"
      @change="onChange"
    />
  </div>
</template>

<script>
import Flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';

const includedEvents = ['onChange', 'onClose', 'onDestroy', 'onKeyDown', 'onMonthChange', 'onOpen', 'onYearChange'];
const excludedEvents = ['onValueUpdate', 'onDayCreate', 'onParseConfig', 'onReady', 'onPreCalendarPosition'];
const allEvents = includedEvents.concat(excludedEvents);
const configCallbacks = ['locale', 'showMonths'];
const camelToKebab = (string) => {
  return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};
const arrayify = (obj) => {
  return obj instanceof Array ? obj : [obj];
};

export default {
  props: {
    value: {
      default: null,
      validator(value) {
        return (
          value === null ||
          value instanceof Date ||
          typeof value === 'string' ||
          value instanceof String ||
          value instanceof Array ||
          typeof value === 'number'
        );
      },
    },
    name: {
      type: String,
      default: '',
    },
    inputId: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'single',
    },
    allowIcon: {
      type: Boolean,
      default: true,
    },
    allowInput: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    altInput: {
      type: Boolean,
      default: true,
    },
    altFormat: {
      type: String,
      default: 'M j, Y',
    },
    dateFormat: {
      type: String,
      default: 'Y-m-d',
    },
    enableTime: {
      type: Boolean,
      default: false,
    },
    noCalendar: {
      type: Boolean,
      default: false,
    },
    minuteIncrement: {
      type: Number,
      default: 30,
    },
    /* eslint-disable-next-line vue/prop-name-casing */
    AppendTo: {
      type: String,
      default: null,
    },
    defaultDate: {
      type: [Date, String],
      default: null,
    },
    minDate: {
      type: [Date, String],
      default: null,
    },
    maxDate: {
      type: [Date, String],
      default: null,
    },
    nextArrow: {
      type: String,
      default: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M5.88 4.12L13.76 12l-7.88 7.88L8 22l10-10L8 2z"/></svg>`,
    },
    prevArrow: {
      type: String,
      default: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M11.67 3.87L9.9 2.1 0 12l9.9 9.9 1.77-1.77L3.54 12z"/></svg>`,
    },
    qaid: {
      type: String,
      default: 'date-picker',
    },
    events: {
      type: Array,
      default: () => includedEvents,
    },
  },
  data() {
    return {
      fp: null,
      config: {
        mode: this.mode,
        allowInput: this.allowInput,
        inline: this.inline,
        altInput: this.altInput,
        altFormat: this.altFormat,
        dateFormat: this.dateFormat,
        appendTo: this.appendTo,
        defaultDate: this.defaultDate,
        nextArrow: this.nextArrow,
        prevArrow: this.prevArrow,
        enableTime: this.enableTime,
        noCalendar: this.noCalendar,
        minuteIncrement: this.minuteIncrement,
        minDate: this.minDate,
        maxDate: this.maxDate,
      },
    };
  },
  watch: {
    config: {
      deep: true,
      handler(newConfig) {
        const config = {...newConfig};

        // Workaround: Don't pass hooks to configs again otherwise
        // previously registered hooks will stop working
        // Notice: we are looping through all events
        // This also means that new callbacks can not passed once component has been initialized
        allEvents.forEach((hook) => {
          delete config[hook];
        });

        this.fp.set(config);

        // Workaround: Allow to change locale dynamically
        configCallbacks.forEach((name) => {
          if (typeof config[name] !== 'undefined') {
            this.fp.set(name, config[name]);
          }
        });
      },
    },

    value(newValue) {
      if (newValue === this.$el.value) return;

      this.fp && this.fp.setDate(newValue, true);
    },
  },
  mounted() {
    if (this.fp) return;
    const config = {...this.config};

    this.events.forEach((hook) => {
      config[hook] = arrayify(config[hook] || []).concat((...args) => {
        this.$emit(camelToKebab(hook), ...args);
      });
    });

    config.defaultDate = this.value || config.defaultDate;
    this.fp = new Flatpickr(this.$refs.picker, config);
    this.fpInput().addEventListener('blur', this.onBlur);
  },

  beforeDestroy() {
    if (this.fp) {
      this.fpInput().removeEventListener('blur', this.onBlur);
      this.fp.destroy();
      this.fp = null;
    }
  },
  methods: {
    onInput(event) {
      // Destructuring the array passed by flatpickr to only contain the necessary bits
      const [startDate, endDate] = this.fp.selectedDates;

      if (this.mode === 'range') {
        this.$emit('input', {
          event: event.target.value,
          startDate,
          endDate,
        });
      } else {
        this.$emit('input', {
          event: event.target.value,
          selectedDate: startDate,
        });
      }
    },
    onChange(event) {
      // Destructuring the array passed by flatpickr to only contain the necessary bits
      const [startDate, endDate] = this.fp.selectedDates;

      if (this.mode === 'range') {
        this.$emit('change', {
          event: event.target.value,
          startDate,
          endDate,
        });
      } else {
        this.$emit('change', {
          event: event.target.value,
          selectedDate: startDate,
        });
      }
    },

    fpInput() {
      return this.fp.altInput || this.fp.input;
    },

    onBlur(event) {
      this.$emit('blur', event.target.value);
    },
  },
};
</script>
